import React from 'react'
import { Mic, Play, Pause, Download, Eye, Clock, Filter, ExternalLink } from 'lucide-react'
import { fetchAdminPodcastsPage, type AdminPodcastDto, type AdminPodcastsPageResponse } from '../api/admin'
import { useQuery } from '@tanstack/react-query'
import Button from '../components/ui/button'
import { useAuth } from '../hooks/use-auth'
import ComboBox, { type ComboBoxOption } from '../components/ui/combobox'
import Tooltip from '../components/ui/tooltip'

const STATUS_OPTIONS: Array<ComboBoxOption<string>> = [
  { value: 'All', label: 'All' },
  { value: 'Draft', label: 'Draft' },
  { value: 'Processing', label: 'Processing' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Failed', label: 'Failed' }
]

const StatCard: React.FC<{ label: string; value: string | number; icon?: React.ReactNode }> = ({ label, value, icon }) => (
  <div className="rounded-xl border border-secondary-200 bg-background p-4">
    <div className="flex items-center justify-between">
      <p className="text-sm text-foreground/60">{label}</p>
      {icon}
    </div>
    <p className="mt-2 text-2xl font-semibold text-foreground">{value}</p>
  </div>
)

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'bg-success-50 text-success-700 ring-success-200'
    case 'processing':
      return 'bg-info-50 text-info-700 ring-info-200'
    case 'failed':
      return 'bg-danger-50 text-danger-700 ring-danger-200'
    case 'draft':
    default:
      return 'bg-secondary-50 text-secondary-700 ring-secondary-200'
  }
}

const getSourceTypeColor = (sourceType: string) => {
  switch (sourceType.toLowerCase()) {
    case 'url':
      return 'bg-brand-50 text-brand-700 ring-brand-200'
    case 'prompt':
      return 'bg-purple-50 text-purple-700 ring-purple-200'
    case 'document':
      return 'bg-orange-50 text-orange-700 ring-orange-200'
    default:
      return 'bg-secondary-50 text-secondary-700 ring-secondary-200'
  }
}

const formatDuration = (seconds?: number) => {
  if (!seconds) return '—'
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

interface PodcastDetailsModalProps {
  podcast: AdminPodcastDto
  isOpen: boolean
  onClose: () => void
}

const PodcastDetailsModal: React.FC<PodcastDetailsModalProps> = ({ podcast, isOpen, onClose }) => {
  const [isPlaying, setIsPlaying] = React.useState(false)
  const audioRef = React.useRef<HTMLAudioElement>(null)

  React.useEffect(() => {
    if (!isOpen) {
      setIsPlaying(false)
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
      }
    }
  }, [isOpen])

  const handlePlayPause = () => {
    if (!audioRef.current || !podcast.audioFileUrl) return

    if (isPlaying) {
      audioRef.current.pause()
      setIsPlaying(false)
    } else {
      audioRef.current.play()
      setIsPlaying(true)
    }
  }

  const handleDownload = () => {
    if (!podcast.audioFileUrl) return

    const link = document.createElement('a')
    link.href = podcast.audioFileUrl
    link.download = `${podcast.title}.mp3`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-xl border border-secondary-200 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <h2 className="text-xl font-semibold text-foreground">Podcast Details</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            ×
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground/60">Title</label>
                <p className="text-foreground font-medium">{podcast.title}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground/60">User</label>
                <p className="text-foreground">{podcast.userName}</p>
                <p className="text-sm text-foreground/60">{podcast.userEmail}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground/60">Status</label>
                <div className="mt-1">
                  <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset ${getStatusColor(podcast.status)}`}>
                    {podcast.status}
                    {podcast.isGenerating && ' (Generating...)'}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground/60">Source Type</label>
                <div className="mt-1">
                  <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset ${getSourceTypeColor(podcast.sourceType)}`}>
                    {podcast.sourceType}
                  </span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground/60">Style & Duration</label>
                <p className="text-foreground capitalize">{podcast.style} • {podcast.durationMinutes}min</p>
                {podcast.durationSeconds && (
                  <p className="text-sm text-foreground/60">Actual: {formatDuration(podcast.durationSeconds)}</p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-foreground/60">Language & Voice</label>
                <p className="text-foreground">{podcast.languageIso}</p>
                <p className="text-sm text-foreground/60">Voices: {podcast.hostAVoiceId}, {podcast.hostBVoiceId}</p>
                <div className="flex gap-2 mt-1 text-xs text-foreground/60">
                  <span>Speed: {podcast.speed}x</span>
                  <span>Vol: {podcast.volume}x</span>
                  <span>Emotion: {podcast.emotion}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          {podcast.description && (
            <div>
              <label className="text-sm font-medium text-foreground/60">Description</label>
              <p className="text-foreground mt-1">{podcast.description}</p>
            </div>
          )}

          {/* Additional Instructions */}
          {podcast.additionalInstructions && (
            <div>
              <label className="text-sm font-medium text-foreground/60">Additional instructions</label>
              <div className="mt-1 p-3 bg-secondary-50 rounded-lg max-h-48 overflow-y-auto">
                <p className="text-sm text-foreground whitespace-pre-wrap break-words">{podcast.additionalInstructions}</p>
              </div>
            </div>
          )}

          {/* Source Payload */}
          <div>
            <label className="text-sm font-medium text-foreground/60">Source Input</label>
            <div className="mt-1 p-3 bg-secondary-50 rounded-lg max-h-32 overflow-y-auto">
              <p className="text-sm text-foreground break-words">{podcast.sourcePayload}</p>
            </div>
          </div>

          {/* Lines Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard label="Total Lines" value={podcast.totalLines} />
            <StatCard label="Completed" value={podcast.completedLines} />
            <StatCard label="Processing" value={podcast.processingLines} />
            <StatCard label="Failed" value={podcast.failedLines} />
            <StatCard label="Draft" value={podcast.draftLines} />
          </div>

          {/* Audio Player */}
          {podcast.audioFileUrl && (
            <div className="border border-secondary-200 rounded-lg p-4">
              <label className="text-sm font-medium text-foreground/60">Generated Audio</label>
              <div className="flex items-center gap-3 mt-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handlePlayPause}
                  className="cursor-pointer"
                >
                  {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  {isPlaying ? 'Pause' : 'Play'}
                </Button>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleDownload}
                  className="cursor-pointer"
                >
                  <Download className="h-4 w-4" />
                  Download
                </Button>

                <a
                  href={podcast.audioFileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-brand-600 hover:text-brand-700 flex items-center gap-1"
                >
                  <ExternalLink className="h-3 w-3" />
                  Direct Link
                </a>
              </div>

              <audio
                ref={audioRef}
                src={podcast.audioFileUrl}
                onEnded={() => setIsPlaying(false)}
                onError={() => setIsPlaying(false)}
                className="w-full mt-3"
                controls
              />
            </div>
          )}

          {/* Podcast Lines - Always render */}
          <div className="border border-secondary-200 rounded-lg p-4">
            <label className="text-sm font-medium text-foreground/60">Podcast Script</label>
            <div className="mt-3">
              {podcast.podcastLines.length > 0 ? (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {podcast.podcastLines.map((line) => (
                    <div
                      key={line.id}
                      className={`flex ${line.host === 'A' ? 'justify-start' : 'justify-end'}`}
                    >
                      <div className={`max-w-[70%] ${line.host === 'A'
                          ? 'bg-brand-50 text-brand-900'
                          : 'bg-secondary-50 text-secondary-900'
                        } rounded-lg p-3`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-foreground/60">
                            {line.host === 'A' ? 'Speaker A' : 'Speaker B'}
                          </span>
                          <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ${getStatusColor(line.status)}`}>
                            {line.status}
                          </span>
                        </div>
                        <p className="text-sm leading-relaxed">{line.text}</p>
                        {line.audioFileUrl && (
                          <div className="mt-2 pt-2 border-t border-current/10">
                            <audio
                              src={line.audioFileUrl}
                              controls
                              className="w-full h-8"
                              style={{ fontSize: '12px' }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-foreground/60 italic">No script lines available yet.</p>
              )}
            </div>
          </div>

          {/* Timestamps */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-foreground/60">
            <div>
              <label className="font-medium">Created</label>
              <p>{new Date(podcast.createdAt).toLocaleString()}</p>
            </div>
            <div>
              <label className="font-medium">Updated</label>
              <p>{new Date(podcast.updatedAt).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const PodcastsTable: React.FC<{
  podcasts: AdminPodcastDto[]
  onViewDetails: (podcast: AdminPodcastDto) => void
}> = ({ podcasts, onViewDetails }) => {
  return (
    <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-background">
      <table className="w-full text-sm">
        <thead className="text-left text-foreground/60">
          <tr>
            <th className="px-4 py-3">Podcast</th>
            <th className="px-4 py-3">User</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Source</th>
            <th className="px-4 py-3">Duration</th>
            <th className="px-4 py-3">Lines</th>
            <th className="px-4 py-3">Created</th>
            <th className="px-4 py-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {podcasts.map(p => (
            <tr key={p.id} className="border-t border-secondary-100">
              <td className="px-4 py-3">
                <div className="flex flex-col max-w-xs">
                  <span className="font-medium text-foreground truncate">{p.title}</span>
                  <span className="text-xs text-foreground/60 capitalize">{p.style}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-col">
                  <span className="text-foreground">{p.userName}</span>
                  <span className="text-xs text-foreground/60">{p.userEmail}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-col gap-1">
                  <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset ${getStatusColor(p.status)} w-fit`}>
                    {p.status}
                  </span>
                  {p.isGenerating && (
                    <span className="text-xs text-info-600">Generating...</span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset ${getSourceTypeColor(p.sourceType)}`}>
                  {p.sourceType}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-col">
                  <span className="text-foreground">{p.durationMinutes}min</span>
                  {p.durationSeconds && (
                    <span className="text-xs text-foreground/60">{formatDuration(p.durationSeconds)}</span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-col text-xs">
                  <span className="text-foreground">{p.totalLines} total</span>
                  <div className="flex gap-1 mt-1">
                    <span className="text-success-600">{p.completedLines}</span>
                    <span className="text-info-600">{p.processingLines}</span>
                    <span className="text-danger-600">{p.failedLines}</span>
                    <span className="text-secondary-600">{p.draftLines}</span>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3">
                <Tooltip content={new Date(p.createdAt).toLocaleString()}>
                  <span className="text-foreground/60">{new Date(p.createdAt).toLocaleDateString()}</span>
                </Tooltip>
              </td>
              <td className="px-4 py-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onViewDetails(p)}
                  className="cursor-pointer"
                >
                  <Eye className="h-4 w-4" />
                  View
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const AdminPodcastsPage: React.FC = () => {
  const { user } = useAuth()
  const [page, setPage] = React.useState(1)
  const [statusFilter, setStatusFilter] = React.useState<string>('All')
  const [selectedPodcast, setSelectedPodcast] = React.useState<AdminPodcastDto | null>(null)
  const pageSize = 20

  const podcastsQuery = useQuery<AdminPodcastsPageResponse, Error, AdminPodcastsPageResponse, [string, string, number, number, string]>({
    queryKey: ['admin', 'podcasts', page, pageSize, statusFilter],
    queryFn: () => fetchAdminPodcastsPage(page, pageSize, statusFilter),
    enabled: !!user?.isAdmin,
    placeholderData: (prev) => prev,
  })

  // Early non-admin view without using hooks conditionally
  const notAdminView = !user?.isAdmin ? (
    <div className="rounded-xl border border-secondary-200 bg-background p-6">
      <p className="text-foreground">You do not have access to this page.</p>
    </div>
  ) : null

  const podcasts = React.useMemo(() => podcastsQuery.data?.podcasts ?? [], [podcastsQuery.data])
  const totalPodcasts = podcastsQuery.data?.totalPodcasts ?? 0

  // Calculate stats from current page data
  const stats = React.useMemo(() => {
    const completed = podcasts.filter(p => p.status === 'Completed').length
    const processing = podcasts.filter(p => p.status === 'Processing').length
    const failed = podcasts.filter(p => p.status === 'Failed').length
    const draft = podcasts.filter(p => p.status === 'Draft').length

    return { completed, processing, failed, draft }
  }, [podcasts])

  return notAdminView ?? (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">Admin Podcasts</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard
          label="Total Podcasts"
          value={totalPodcasts}
          icon={<Mic className="h-4 w-4 text-foreground/40" />}
        />
        <StatCard label="Completed" value={stats.completed} />
        <StatCard label="Processing" value={stats.processing} />
        <StatCard label="Failed" value={stats.failed} />
        <StatCard label="Draft" value={stats.draft} />
      </div>

      {/* Filters and pagination */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-foreground/60" />
            <ComboBox
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Filter by status"
              buttonClassName="w-40"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={page === 1 || podcastsQuery.isLoading}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            className="cursor-pointer"
          >
            Prev
          </Button>
          <span className="text-sm text-foreground/60">
            Page {page}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={podcastsQuery.isLoading || (page * pageSize >= totalPodcasts)}
            onClick={() => setPage(p => p + 1)}
            className="cursor-pointer"
          >
            Next
          </Button>
        </div>
      </div>

      {/* Podcasts table */}
      <div className="space-y-3">
        {podcastsQuery.isLoading ? (
          <div className="h-96 rounded-xl border border-secondary-200 bg-background grid place-items-center text-foreground/60">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 animate-spin" />
              Loading podcasts...
            </div>
          </div>
        ) : podcasts.length === 0 ? (
          <div className="h-96 rounded-xl border border-secondary-200 bg-background grid place-items-center text-foreground/60">
            <div className="text-center">
              <Mic className="h-12 w-12 mx-auto mb-4 text-foreground/20" />
              <p className="text-lg font-medium">No podcasts found</p>
              <p className="text-sm">Try adjusting your filters</p>
            </div>
          </div>
        ) : (
          <PodcastsTable
            podcasts={podcasts}
            onViewDetails={setSelectedPodcast}
          />
        )}
      </div>

      {/* Details Modal */}
      {selectedPodcast && (
        <PodcastDetailsModal
          podcast={selectedPodcast}
          isOpen={!!selectedPodcast}
          onClose={() => setSelectedPodcast(null)}
        />
      )}
    </div>
  )
}

export default AdminPodcastsPage